import React, { useState } from 'react';
import { 
    Avatar, 
    Button, 
    Menu, 
    MenuTrigger, 
    MenuPopover, 
    MenuList, 
    MenuItem, 
    MenuDivider,
    Text,
    Switch,
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogContent,
    DialogActions,
    Link
} from "@fluentui/react-components";
import { 
    SettingsRegular, 
    QuestionCircleRegular, 
    SignOutRegular
} from "@fluentui/react-icons";
import { useMsal } from '@azure/msal-react';
import './Header.css';

interface HeaderProps {
    setDarkTheme: React.Dispatch<React.SetStateAction<boolean>>
    darkTheme: boolean
}

function Header(props: HeaderProps) {
    const { accounts, instance } = useMsal();
    const [isSignOutDialogOpen, setIsSignOutDialogOpen] = useState(false);
    
    const onThemeChange = (ev: React.ChangeEvent<HTMLInputElement>, data: { checked: boolean }) => {
        props.setDarkTheme(data.checked);
    };

    const handleSignOut = () => {
        setIsSignOutDialogOpen(false);
        instance.logoutRedirect();
    };

    return (
        <header style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            padding: '0 1.25rem', 
            minHeight: '3rem', 
            borderBottom: '1px solid #edebe9',
            backgroundColor: '#0078d4',
            color: 'white',
            flexWrap: 'wrap'
        }}>
            <h1 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'white' }}>e-Invoicing Dashboard</h1>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                {/* Settings */}
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button appearance="subtle" icon={<SettingsRegular />} aria-label="Settings" aria-description="Open settings menu for theme preferences" className="header-button" style={{ color: 'white', border: '1px solid transparent' }} />
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <MenuItem>
                                <Switch 
                                    checked={props.darkTheme}
                                    onChange={onThemeChange}
                                    label={props.darkTheme ? "Dark Mode" : "Light Mode"}
                                />
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>

                {/* Help */}
                <Dialog>
                    <DialogTrigger disableButtonEnhancement>
                        <Button appearance="subtle" icon={<QuestionCircleRegular />} aria-label="Help" aria-description="Open help dialog for support and privacy information" className="header-button" style={{ color: 'white', border: '1px solid transparent' }} />
                    </DialogTrigger>
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Help</DialogTitle>
                            <DialogContent>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                                    <Text>Contact <Link href="mailto: ctbdevs@microsoft.com">support</Link> for further assistance</Text>
                                    <Link href="https://go.microsoft.com/fwlink/?LinkId=518021" target="_blank" rel="noreferrer">Privacy & Cookies</Link>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <DialogTrigger disableButtonEnhancement>
                                    <Button appearance="secondary">Close</Button>
                                </DialogTrigger>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>

                {/* Profile */}
                <Menu>
                    <MenuTrigger disableButtonEnhancement>
                        <Button appearance="transparent" icon={<Avatar name={accounts[0]?.name} size={28} aria-hidden="true" role="presentation" aria-label="" />} aria-label={accounts[0]?.name || "User profile"} aria-description="Open user profile menu" className="header-button" style={{ color: 'white', border: '1px solid transparent' }} />
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            <MenuItem>
                                <div style={{ display: 'flex', flexDirection: 'column' }} dir="auto" lang="en">
                                    <Text weight="semibold">{accounts[0]?.name}</Text>
                                    <Text size={200}>{accounts[0]?.username}</Text>
                                </div>
                            </MenuItem>
                            <MenuDivider />
                            <MenuItem icon={<SignOutRegular />} onClick={() => setIsSignOutDialogOpen(true)}>
                                Sign out
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>

                {/* WCAG 3.3.8 Authentication: Confirmation dialog prevents accidental sign-out,
                    avoiding the need to re-authenticate. The app uses Azure AD redirect-based SSO
                    which relies on recognition (account picker) rather than recall or transcription.
                    WCAG 3.3.9 Redundant Entry: User identity (name and email) is pre-populated
                    from the current session so the user does not need to re-enter or recall it. */}
                <Dialog
                    open={isSignOutDialogOpen}
                    onOpenChange={(_, data) => setIsSignOutDialogOpen(data.open)}
                >
                    <DialogSurface>
                        <DialogBody>
                            <DialogTitle>Sign out</DialogTitle>
                            <DialogContent>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <Text>Are you sure you want to sign out?</Text>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0' }}>
                                        <Avatar name={accounts[0]?.name} size={40} aria-hidden="true" />
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <Text weight="semibold">{accounts[0]?.name}</Text>
                                            <Text size={200}>{accounts[0]?.username}</Text>
                                        </div>
                                    </div>
                                </div>
                            </DialogContent>
                            <DialogActions>
                                <Button appearance="secondary" onClick={() => setIsSignOutDialogOpen(false)}>Cancel</Button>
                                <Button appearance="primary" icon={<SignOutRegular />} onClick={handleSignOut}>Sign out</Button>
                            </DialogActions>
                        </DialogBody>
                    </DialogSurface>
                </Dialog>
            </div>
        </header>
    );
}
export default Header;
